import { useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';
import union from 'lodash/union';
import { SanitizerService } from '../../../services/sanitizer.service';
import type { ITrafficSituation } from '../../../models/traffic-situation.model';
import ClearButtonComponent from '../../Shared/ClearButton';
import TrafficSituationApiModel from '../models/traffic-situation.api-model';
import { TransportMode } from '../../../models/transport-mode.enum';
import { TrafficSituationHelper } from '../../../helpers/traffic-situation.helper';
import TrafficSituationV2 from './components/TrafficSituationV2';
import { AnalyticsService } from '../../../services/analytics-service';
import TransportModeFilter from '../../Shared/TransportModeFilter';
import ValidationInfo from '../../../validation/validation-info';
import { TransportModeHelper } from '../../../helpers/transport-mode.helper';
import { QueryStringHelper } from '../../../helpers/query-string.helper';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import CheckboxFormGroup from '../../Shared/CheckboxFormGroup';
import MunicipalityApiModel from '../models/municipality.api-model';
import { LineHelper } from '../../../helpers/line.helper';
import { IMultiFacetSelector } from '../../Shared/MultiFacetSelector';
import MultiFacetSelector from '../../Shared/MultiFacetSelector';
import IMultiFacet from '../../../models/multi-facet.model';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';
import { TrafficSituationSeverity } from '../../../models/traffic-situation-severity.enum';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import { t } from '../../../services/texts.service';

export interface ITrafficInformationSearchTexts {
  filterUpdateMessage: string;
  multiFacetSearchLabel: string;
  multiFacetPlaceholder: string;
  multiFacetNoResults: string;
  transportModeLabel: string;
  currentTrafficSituations: string;
  upcomingTrafficSituations: string;
  sortOrderLabel: string;
  linkToDetailsPage: string;
  noTrafficSituationsFound: string;
}

export type ITrafficInformationSearchSettings = object;

type ITrafficInformationSearchProps = IServerSideRendered<ITrafficInformationSearchTexts, ITrafficInformationSearchSettings>;

enum TrafficSituationType {
  current = 'current',
  planned = 'planned',
}

enum SortOrder {
  latest = 'latest',
  severity = 'severity',
}

function processReceivedData(data: TrafficSituationApiModel[]): ITrafficSituation[] {
  return data?.map((ts) => TrafficSituationApiModel.mapToBusinessModel(ts)) ?? [];
}

function filterBasedOnMultiFacetItems(ts: ITrafficSituation, selectedMultiFacetItems: IMultiFacet[]): boolean {
  if (!selectedMultiFacetItems || selectedMultiFacetItems.length === 0) return true; // No filter applied if no value is selected

  // Check if any selected item matches the traffic situation
  return selectedMultiFacetItems.some((item) => {
    if (item.type === MultiFacetType.Line && item.line) {
      const line = item.line;
      // Check if the traffic situation affects the selected line
      return TrafficSituationHelper.getDistinctLines(ts).some((l) => l.gid === line.gid);
    }

    if (item.type === MultiFacetType.Municipality && item.municipality) {
      const municipality = item.municipality;
      // Check if the traffic situation affects the selected municipality
      const allAffectedMunicipalities = union(
        flatten(TrafficSituationHelper.getDistinctLines(ts).map((x) => x.municipalities ?? [])),
        ts.affectedStopPoints.filter((sp) => sp.municipality).map((sp) => sp.municipality!),
      );

      return allAffectedMunicipalities.some((m) => m.gid === municipality.gid);
    }

    return false;
  });
}

function filterBasedOnTransportMode(ts: ITrafficSituation, selectedTransportModes: TransportMode[]): boolean {
  if (ts.affectedLines.length === 0 && ts.affectedJourneys.length === 0) return true;
  return selectedTransportModes.some((tm) => TrafficSituationHelper.affectsLineWithTransportMode(ts, tm));
}

function filterBasedOnSeverity(ts: ITrafficSituation, selectedSeverities: TrafficSituationSeverity[]): boolean {
  return selectedSeverities.includes(ts.severity);
}

function filterBasedOnSituationType(ts: ITrafficSituation, selectedTrafficSituationType: TrafficSituationType[]): boolean {
  const today = DateTime.now().startOf('day');
  if (selectedTrafficSituationType.includes(TrafficSituationType.current) && ts.startTime.startOf('day') <= today && (!ts.endTime || ts.endTime.startOf('day') >= today)) return true;
  if (selectedTrafficSituationType.includes(TrafficSituationType.planned) && ts.startTime.startOf('day') > today) return true;
  return false;
}

function TrafficInformationSearch({ texts }: ITrafficInformationSearchProps) {
  // === CONSTANTS ===
  const allTransportModes = [TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry];

  // === STATE ===
  const [selectedTrafficSituationType, setSelectedTrafficSituationType] = useState<TrafficSituationType[]>([TrafficSituationType.current, TrafficSituationType.planned] as TrafficSituationType[]);
  const [selectedSeverities, setSelectedSeverities] = useState<TrafficSituationSeverity[]>([
    TrafficSituationSeverity.slight,
    TrafficSituationSeverity.normal,
    TrafficSituationSeverity.severe,
  ] as TrafficSituationSeverity[]);
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(SortOrder.latest);
  const [selectedTransportModes, setSelectedTransportModes] = useState<TransportMode[]>(allTransportModes);
  const [selectedMultiFacetItems, setSelectedMultiFacetItems] = useState<IMultiFacet[]>([]);
  const [{ status: getTrafficSituationsStatus, data: getTrafficSituationsData }] = useFetch<TrafficSituationApiModel[]>(`/api/trafficsituations/all`);

  // === REFS ===
  const multiFacetSelectorRef = useRef<IMultiFacetSelector>(null);
  const hasScrolledRef = useRef<boolean>(false);

  // === VARIABLES ===
  const commonSearches = [
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001480', name: 'Göteborg', shortName: 'Göteborg' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001490', name: 'Borås', shortName: 'Borås' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001481', name: 'Mölndal', shortName: 'Mölndal' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001488', name: 'Trollhättan', shortName: 'Trollhättan' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001496', name: 'Skövde', shortName: 'Skövde' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001485', name: 'Uddevalla', shortName: 'Uddevalla' }, line: undefined },
  ];

  const allTrafficSituations: ITrafficSituation[] = useMemo(
    () =>
      getTrafficSituationsStatus !== Status.success || !getTrafficSituationsData
        ? []
        : orderBy(
            processReceivedData(getTrafficSituationsData)
              .filter((ts) => filterBasedOnSituationType(ts, selectedTrafficSituationType))
              .filter((ts) => filterBasedOnTransportMode(ts, selectedTransportModes))
              .filter((ts) => filterBasedOnMultiFacetItems(ts, selectedMultiFacetItems))
              .filter((ts) => filterBasedOnSeverity(ts, selectedSeverities)),
            selectedSortOrder === SortOrder.latest
              ? [(ts) => (TrafficSituationHelper.isNewTrafficSituation(ts) ? 2 : TrafficSituationHelper.isCurrentTrafficSituation(ts) ? 1 : 0), (ts) => ts.startTime]
              : [(ts) => TrafficSituationHelper.toSeveritySortOrder(ts.severity)],
            'desc',
          ),
    [getTrafficSituationsStatus, getTrafficSituationsData, selectedMultiFacetItems, selectedTransportModes, selectedSeverities, selectedTrafficSituationType, selectedSortOrder],
  );

  // === EFFECTS ===
  // NV-8095: Scroll to anchor after data is loaded and rendered
  useEffect(() => {
    if (getTrafficSituationsStatus === Status.success && allTrafficSituations.length > 0 && !hasScrolledRef.current) {
      const hash = window.location.hash;
      if (hash) {
        try {
          const id = decodeURIComponent(hash.replace('#', ''));
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            hasScrolledRef.current = true;
          }
        } catch {
          /* empty */
        }
      }
    }
  }, [getTrafficSituationsStatus, allTrafficSituations]);

  useEffect(() => {
    async function setMunicipalitiesFromQuerystring() {
      const municipalityIds = QueryStringHelper.getValuesFromQueryString('k');
      if (municipalityIds?.length > 0) {
        try {
          const response = await fetch('/api/trafficsituations/municipalities');
          if (!response.ok) throw new Error(response.statusText);

          const municipalities = ((await response.json()) as MunicipalityApiModel[]).map((m) => MunicipalityApiModel.mapToBusinessModel(m));

          const filteredMunicipalities = municipalities.filter((m) => municipalityIds.includes(m.gid));

          setSelectedMultiFacetItems(
            filteredMunicipalities.map((m) => ({
              type: MultiFacetType.Municipality,
              municipality: m,
              line: undefined,
            })),
          );
        } catch {
          /* empty */
        }
      }
    }

    function setTransportModesFromQuerystring() {
      try {
        const transportModes = QueryStringHelper.getValuesFromQueryString('t');
        if (transportModes?.length > 0) {
          setSelectedTransportModes(transportModes.map((transportMode) => TransportModeHelper.parse(transportMode)));
        }
      } catch {
        /* empty */
      }
    }

    function setTrafficSituationTypeFromQuerystring() {
      try {
        const trafficSituationTypes = QueryStringHelper.getValuesFromQueryString('g');
        if (trafficSituationTypes?.length > 0) {
          setSelectedTrafficSituationType(trafficSituationTypes.map((type) => type as TrafficSituationType));
        }
      } catch {
        /* empty */
      }
    }

    setMunicipalitiesFromQuerystring();
    setTransportModesFromQuerystring();
    setTrafficSituationTypeFromQuerystring();
  }, []);

  // ======================================================================
  // Event handlers
  // ======================================================================
  function handleMultiFacetSelect(multiFacetItems: IMultiFacet[]): void {
    if (multiFacetItems.length > 0) {
      setSelectedMultiFacetItems(multiFacetItems);
    } else {
      setSelectedMultiFacetItems([]);
    }

    // Group items by type
    const lines = multiFacetItems
      .filter((item) => item.type === MultiFacetType.Line && item.line)
      .map((item) => {
        const line = item.line!;
        return `${LineHelper.getLineNameAndDirection(line)}, ${line.gid}`;
      });

    const municipalities = multiFacetItems
      .filter((item) => item.type === MultiFacetType.Municipality && item.municipality)
      .map((item) => {
        const municipality = item.municipality!;
        return `${municipality.name}, ${municipality.gid}`;
      });

    // Send a single analytics event for lines
    if (lines.length > 0) {
      AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Linjer', lines.join('; '));
    }

    // Send a single analytics event for municipalities
    if (municipalities.length > 0) {
      AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Kommuner', municipalities.join('; '));
    }
  }

  function handleTransportModeFilterChange(transportModes: TransportMode[]): void {
    setSelectedTransportModes(transportModes);
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Trafikslag', transportModes.map((transportMode: TransportMode) => transportMode.toString()).toString());
  }

  function handleClearClick(): void {
    setSelectedTransportModes(allTransportModes);
    multiFacetSelectorRef.current!.clear();
    setSelectedMultiFacetItems([]);
    setSelectedTrafficSituationType([TrafficSituationType.current, TrafficSituationType.planned]);
    setSelectedSeverities([TrafficSituationSeverity.slight, TrafficSituationSeverity.normal, TrafficSituationSeverity.severe]);
    setSelectedSortOrder(SortOrder.latest);
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på återställ', '');
  }

  return (
    <div className="traffic-information-search">
      <div className="traffic-information-search__filters bg-sm-white shadow-sm rounded p-sm-3 mb-4">
        <div className="mb-3">
          {texts.filterUpdateMessage && <div className="bg-gray p-3 mb-3">{texts.filterUpdateMessage}</div>}
          <MultiFacetSelector
            id="multi-facet-selector"
            ref={multiFacetSelectorRef}
            labelText={texts.multiFacetSearchLabel}
            placeholderText={texts.multiFacetPlaceholder}
            noResultsText={texts.multiFacetNoResults || t('Inga sökresultat', 'No search results')}
            onChange={handleMultiFacetSelect}
            state={{ value: selectedMultiFacetItems, validationInfo: new ValidationInfo() }}
            commonSearches={commonSearches}
          />
        </div>

        <fieldset className="mb-3">
          <legend>{texts.transportModeLabel}</legend>
          <TransportModeFilter
            availableTransportModes={[TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry]}
            selectedTransportModes={selectedTransportModes}
            onChange={handleTransportModeFilterChange}
          ></TransportModeFilter>
        </fieldset>

        <CheckboxFormGroup
          id="traffic-situation-type"
          labelText={t('Giltighetstid', 'Validity period')}
          rowLayoutMdUp={true}
          className="mb-3"
          options={[
            { value: TrafficSituationType.current, text: texts.currentTrafficSituations },
            { value: TrafficSituationType.planned, text: texts.upcomingTrafficSituations },
          ]}
          state={{ value: selectedTrafficSituationType, validationInfo: new ValidationInfo() }}
          onChange={(value: string[]) => setSelectedTrafficSituationType(value as TrafficSituationType[])}
        ></CheckboxFormGroup>

        <RadioFormGroup
          id="sort-order"
          labelText={texts.sortOrderLabel}
          rowLayout={true}
          rowLayoutSmUp={true}
          className="mb-3"
          options={[
            { value: SortOrder.latest, text: t('Senaste', 'Latest') },
            { value: SortOrder.severity, text: t('Störst trafikpåverkan', 'Greatest severity') },
          ]}
          state={{ value: selectedSortOrder, validationInfo: new ValidationInfo() }}
          onChange={(value: string) => setSelectedSortOrder(value as SortOrder)}
        ></RadioFormGroup>
        <div>
          <ClearButtonComponent onClick={handleClearClick}></ClearButtonComponent>
        </div>
      </div>

      {getTrafficSituationsStatus === Status.loading && (
        <div className="mb-4 pt-3">
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        </div>
      )}

      {getTrafficSituationsStatus !== Status.loading && (
        <div>
          <div className="accordion">
            <h2 aria-live="polite">
              <span>
                {allTrafficSituations.length === 0 ? (
                  <span>
                    <span className="visually-hidden">{allTrafficSituations.length}</span> {t('Trafikstörningar', 'Traffic disruptions')}
                  </span>
                ) : (
                  `${allTrafficSituations.length} ${allTrafficSituations.length === 1 ? t('trafikstörning', 'traffic disruption') : t('trafikstörningar', 'traffic disruptions')}`
                )}
              </span>
            </h2>
            {allTrafficSituations.length > 0 &&
              allTrafficSituations.map((ts, index) => (
                <div id={ts.situationNumber} key={ts.situationNumber} className={`${index < allTrafficSituations.length - 1 ? 'mb-3' : ''} ${index === 0 ? 'mt-3' : ''}`}>
                  <TrafficSituationV2 trafficSituation={ts} texts={{ linkToDetailsPage: texts.linkToDetailsPage }} />
                </div>
              ))}
            {!allTrafficSituations.length && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.noTrafficSituationsFound) }}></div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrafficInformationSearch;
